<template>
  <section class="container-fluid">
    <vue-basic-alert :duration="300" :closeIn="5000" ref="alert" />
    <div class="d-flex justify-content-between align-items-center my-3">
      <h4 class="mb-0">SKUs - Gestión WOE</h4>
      <router-link :to="route" class="d-none d-sm-inline-block">
        <img
          :src="require('@/assets/img/backspace.svg')"
          alt="backspace"
          height="28"
        />
      </router-link>
    </div>

    <div class="row mb-3">
      <div class="col-12 col-md-8">
        <div class="input-group mb-2">
          <input
            type="text"
            class="form-control"
            v-model="query"
            placeholder="Buscar por EAN o nombre"
            @keyup.enter="fetchSkus(true)"
          />
          <div class="input-group-append">
            <button
              class="btn btn-primary"
              type="button"
              @click="fetchSkus(true)"
            >
              Buscar
            </button>
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="$router.push(route + 'skus/form')"
            >
              Nuevo SKU
            </button>
          </div>
        </div>
        <div v-if="availableCodeKeys.length > 1">
          <select
            class="form-control"
            v-model="codeKey"
            @change="fetchSkus(true)"
            style="max-width: 200px"
          >
            <option v-for="codeKey in availableCodeKeys" :key="codeKey.value" :value="codeKey.value">
              {{ codeKey.label }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center my-4">
      <div class="d-inline-flex align-items-center">
        <svg
          class="text-primary"
          width="1.2rem"
          height="1.2rem"
          viewBox="0 0 50 50"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="currentColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-dasharray="31.4 94.2"
          >
            <animateTransform
              attributeType="xml"
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </div>
    </div>

    <div v-else>
      <div v-if="skus.length === 0" class="alert alert-info">
        No se encontraron SKUs
      </div>

      <div v-else>
        <div class="sku-table-wrapper">
          <div
            v-show="showTopScrollbar"
            ref="topScroll"
            class="top-scrollbar"
            @scroll="onTopScroll"
          >
            <div ref="topInner" class="top-inner"></div>
          </div>

          <div ref="tableScroll" class="table-scroll" @scroll="onTableScroll">
            <table ref="tableRef" class="table table-striped table-bordered">
              <thead class="thead-light">
                <tr>
                  <th>Nombre</th>
                  <th>Código SAP</th>
                  <th>EAN</th>
                  <th>Estado WOE</th>
                  <th>Bolsas por caja</th>
                  <th>Peso neto (kg)</th>
                  <th>Volumen (cm³)</th>
                  <th>Peso bruto (kg)</th>
                  <th>Cajas por pallet</th>
                  <th v-if="hasSalesUnits">Unidad venta 1</th>
                  <th v-if="hasSalesUnits">Unidad venta 2</th>
                  <th v-if="isColombia">Despliegue</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sku in skus" :key="sku.id">
                  <td>{{ sku.name }}</td>
                  <td>{{ sku.description4 }}</td>
                  <td>{{ sku.codes[this.codeKey] }}</td>
                  <td>
                    {{ sku.description1 === "TRUE" ? "Activo" : "Inactivo" }}
                  </td>
                  <td>{{ sku.sellingQuantities }}</td>
                  <td>{{ formatDecimals(sku.numberOfUnits) }}</td>
                  <td>{{ formatDecimals(sku.description10) }}</td>
                  <td>{{ formatDecimals(sku.description12) }}</td>
                  <td>{{ sku.description5 }}</td>
                  <td v-if="hasSalesUnits">{{ sku.description19 }}</td>
                  <td v-if="hasSalesUnits">{{ sku.description20 }}</td>
                  <td v-if="isColombia">{{ sku.description21 }}</td>
                  <td>
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="editSku(sku)"
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-if="isLoadingMore" class="text-center my-3">
        <div class="d-inline-flex align-items-center">
          <svg
            class="text-primary"
            width="1.2rem"
            height="1.2rem"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="currentColor"
              stroke-width="5"
              stroke-linecap="round"
              stroke-dasharray="31.4 94.2"
            >
              <animateTransform
                attributeType="xml"
                attributeName="transform"
                type="rotate"
                from="0 25 25"
                to="360 25 25"
                dur="1s"
                repeatCount="indefinite"
              />
            </circle>
          </svg>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { route } from "../../enviroment.js";
import skuService from "../../services/skuService";
import { showFlashFromRoute } from "../../utils/flashAlert";

export default {
  name: "SkuList",
  data() {
    return {
      skus: [],
      codeKey: "",
      availableCodeKeys: [],
      loading: false,
      query: "",
      showTopScrollbar: false,
      // pagination with limit/offset for infinite scroll
      limit: 25,
      offset: 0,
      finished: false,
      isLoadingMore: false,
      route: route,
    };
  },
  computed: {
    isColombia() {
      const country = sessionStorage.getItem("country") || "";
      return country.toLowerCase().includes("colombia");
    },
    isUruguay() {
      const country = sessionStorage.getItem("country") || "";
      return country.toLowerCase().includes("uruguay");
    },
    hasSalesUnits() {
      return this.isColombia || this.isUruguay;
    },
  },
  mounted() {
    this.fetchSkus(true);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    // ensure scroll sync state after initial render
    this.$nextTick(() => {
      this.checkOverflow();
    });
    // central flash handling helper
    try {
      showFlashFromRoute(this, "alert");
    } catch (e) {
      // ignore
    }
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    // remove any element listeners (Vue listeners bound in template are cleaned up automatically)
  },
  watch: {
    skus() {
      this.$nextTick(() => this.checkOverflow());
    },
  },
  methods: {
    async fetchSkus(reset = false) {
      try {
        if (reset) {
          this.offset = 0;
          this.skus = [];
          this.finished = false;
        }

        if (this.finished) return;

        // decide which loading indicator to use
        const firstLoad = this.offset === 0 && !this.isLoadingMore;
        if (firstLoad) this.loading = true;
        else this.isLoadingMore = true;

        let params = {};
        if (this.query && this.query.trim().length > 0) {
          params.query = this.query.trim();
        } else {
          params = {
            limit: this.limit,
            offset: this.offset,
          };
        }

        params = { ...params, codeKey: this.codeKey };

        const resp = await skuService.getSkus(params);

        let data = [];
        data = resp.data.data;

        this.skus = this.skus.concat(data.skus);
        this.offset += data.skus.length;
        this.codeKey = data.codeKey;
        this.availableCodeKeys = data.availableCodeKeys;

        // if returned less than limit, assume no more data
        if (data.skus.length < this.limit) this.finished = true;
      } catch (err) {
        console.error("Error fetching SKUs", err);
      } finally {
        this.loading = false;
        this.isLoadingMore = false;
      }
    },
    editSku(sku) {
      this.$router.push(
        this.route +
          `skus/form?ean=${encodeURIComponent(
            sku.codes[this.codeKey]
          )}&codeKey=${encodeURIComponent(this.codeKey)}`
      );
    },
    onTopScroll() {
      const top = this.$refs.topScroll;
      const bottom = this.$refs.tableScroll;
      if (top && bottom) bottom.scrollLeft = top.scrollLeft;
    },
    onTableScroll() {
      const top = this.$refs.topScroll;
      const bottom = this.$refs.tableScroll;
      if (top && bottom) top.scrollLeft = bottom.scrollLeft;
    },
    checkOverflow() {
      const table = this.$refs.tableRef;
      const tableScroll = this.$refs.tableScroll;
      const topInner = this.$refs.topInner;
      if (!table || !tableScroll || !topInner) {
        this.showTopScrollbar = false;
        return;
      }

      // determine if horizontal overflow exists
      const need = table.scrollWidth > tableScroll.clientWidth + 1;
      this.showTopScrollbar = need;

      // set spacer width to match table full width so top scrollbar mirrors
      topInner.style.width = table.scrollWidth + "px";
    },
    formatDecimals(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "";
      return parseFloat(n.toFixed(4));
    },
    handleScroll() {
      if (this.query) return;
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 300;
      if (
        nearBottom &&
        !this.loading &&
        !this.isLoadingMore &&
        !this.finished
      ) {
        this.fetchSkus(false);
      }
    },
  },
};
</script>

<style scoped>
.sku-table-wrapper {
  display: flex;
  flex-direction: column;
}
.top-scrollbar {
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
}
.top-inner {
  height: 1px;
}
.table-scroll {
  overflow-x: auto;
}

/* keep table full-width behavior */
.table-scroll table {
  margin-bottom: 0;
}
</style>
